import { motion } from "framer-motion";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";

const HomePage = () => {
  return (
    <div className="relative min-h-screen" data-testid="homepage">
      <Navigation />
      
      {/* Arc Line SVG */}
      <div className="arc-line hidden lg:block">
        <svg viewBox="0 0 800 900" preserveAspectRatio="none">
          <defs>
            <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="30%" stopColor="#bd9a59" />
              <stop offset="70%" stopColor="#d4b270" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
          </defs>
          <path
            className="arc-path"
            d="M 800 100 Q 400 200 500 450 Q 600 700 800 600"
          />
        </svg>
      </div>

      {/* Top Badge */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="absolute top-32 left-8 z-20"
      >
        <div 
          className="glass-card px-5 py-2.5 rounded-sm"
          data-testid="location-badge"
        >
          <span className="text-sm font-medium text-slate-200 tracking-wide">
            Australia <span className="text-[#bd9a59] mx-1">•</span> Global
          </span>
        </div>
      </motion.div>

      {/* Main Content */}
      <main className="relative z-10 px-8 lg:px-16 pt-48 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-3xl"
          >
            <h1 
              className="font-['Playfair_Display'] text-5xl md:text-6xl lg:text-7xl font-medium text-white tracking-tight leading-[1.1] mb-4"
              data-testid="main-heading"
            >
              Solara Global Capital
            </h1>
            <h2 
              className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-normal text-[#bd9a59] tracking-tight mb-8"
              data-testid="sub-heading"
            >
              Funding Australia's Property Leaders.
            </h2>
            <p 
              className="text-lg md:text-xl font-light leading-relaxed text-slate-300 max-w-2xl mb-10"
              data-testid="hero-description"
            >
              Partner-led finance brokerage and capital advisory for developers
              and strategic acquirers — delivered with private bank style service,
              institutional discipline, and an AI-enabled process that provides
              clarity and certainty.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-16">
              <Link to="/booking">
                <Button
                  className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-8 py-6 text-sm font-semibold tracking-wide uppercase transition-all duration-300 shadow-[0_0_20px_rgba(189,154,89,0.3)] hover:shadow-[0_0_30px_rgba(189,154,89,0.5)]"
                  data-testid="book-call-btn"
                >
                  Book a Partner Call
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-8 py-6 text-sm font-semibold tracking-wide uppercase transition-all"
                  data-testid="request-assessment-btn"
                >
                  Request Funding Assessment
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Feature Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-wrap gap-3 mb-12"
            data-testid="feature-badges"
          >
            {[
              "Partner-led",
              "AI-enabled execution",
              "Developer-grade funding",
              "Syndicated mortgages (roadmap)"
            ].map((badge, index) => (
              <div
                key={badge}
                className="feature-badge px-5 py-3 rounded-sm"
                data-testid={`feature-badge-${index}`}
              >
                <span className="text-sm text-slate-200 font-medium">
                  {badge}
                </span>
              </div>
            ))}
          </motion.div>

          {/* Premium Property Finance Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="absolute right-8 lg:right-16 top-1/2 transform -translate-y-1/3 hidden lg:block max-w-md"
          >
            <div 
              className="premium-card glass-card p-8 rounded-sm tracing-border"
              data-testid="premium-card"
            >
              <h3 className="text-[#bd9a59] text-sm font-semibold tracking-wider uppercase mb-4">
                Premium Property Finance
              </h3>
              <h4 className="font-['Playfair_Display'] text-xl text-white font-medium mb-3">
                Fast clarity. Institutional polish.
              </h4>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                We package deals like an institutional desk:
                feasibility discipline, lender-fit strategy, and
                a transparent workflow — so you move with
                confidence.
              </p>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-white font-semibold text-lg mb-1">Developer</div>
                  <div className="text-slate-500 text-xs uppercase tracking-wider">Funding focus</div>
                </div>
                <div className="text-center border-l border-r border-white/10">
                  <div className="text-white font-semibold text-lg mb-1">AI</div>
                  <div className="text-slate-500 text-xs uppercase tracking-wider">Workflow advantage</div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold text-lg mb-1">Long-term</div>
                  <div className="text-slate-500 text-xs uppercase tracking-wider">Partner mindset</div>
                </div>
              </div>

              <Link 
                to="/services" 
                className="inline-flex items-center text-white text-sm font-medium hover:text-[#bd9a59] transition-colors group"
                data-testid="see-how-it-works-link"
              >
                See how it works
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>

          {/* Mobile Premium Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="lg:hidden mt-8"
          >
            <div 
              className="premium-card glass-card p-6 rounded-sm"
              data-testid="premium-card-mobile"
            >
              <h3 className="text-[#bd9a59] text-sm font-semibold tracking-wider uppercase mb-3">
                Premium Property Finance
              </h3>
              <h4 className="font-['Playfair_Display'] text-lg text-white font-medium mb-2">
                Fast clarity. Institutional polish.
              </h4>
              <p className="text-slate-400 text-sm leading-relaxed mb-4">
                We package deals like an institutional desk:
                feasibility discipline, lender-fit strategy, and
                a transparent workflow — so you move with confidence.
              </p>

              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center">
                  <div className="text-white font-semibold text-sm mb-0.5">Developer</div>
                  <div className="text-slate-500 text-[10px] uppercase tracking-wider">Funding focus</div>
                </div>
                <div className="text-center border-l border-r border-white/10">
                  <div className="text-white font-semibold text-sm mb-0.5">AI</div>
                  <div className="text-slate-500 text-[10px] uppercase tracking-wider">Workflow</div>
                </div>
                <div className="text-center">
                  <div className="text-white font-semibold text-sm mb-0.5">Long-term</div>
                  <div className="text-slate-500 text-[10px] uppercase tracking-wider">Partner</div>
                </div>
              </div>

              <Link 
                to="/services" 
                className="inline-flex items-center text-white text-sm font-medium hover:text-[#bd9a59] transition-colors"
              >
                See how it works
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1 }}
        className="fixed bottom-0 left-0 right-0 z-20 px-8 lg:px-16 py-6"
        data-testid="footer"
      >
        <div className="max-w-7xl mx-auto">
          <div className="glass-card px-6 py-4 rounded-sm flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <MapPin className="w-4 h-4 text-[#bd9a59]" />
              <span>Head Office: Level 1, 60 Martin Place, Sydney</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 lg:gap-6">
              <a 
                href="tel:1300021888" 
                className="flex items-center gap-2 text-slate-300 text-sm hover:text-[#bd9a59] transition-colors"
                data-testid="phone-link"
              >
                <Phone className="w-4 h-4" />
                <span>Call: 1300 021 888</span>
              </a>
              <a 
                href="mailto:loans@sgcap.com.au" 
                className="flex items-center gap-2 text-slate-300 text-sm hover:text-[#bd9a59] transition-colors"
                data-testid="email-link"
              >
                <Mail className="w-4 h-4" />
                <span>loans@sgcap.com.au</span>
              </a>
              <a
                href="https://app.solaraglobal.au/apply"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] px-4 py-2 text-xs font-semibold tracking-wide uppercase transition-all rounded-sm flex items-center gap-2"
                data-testid="loan-application-btn"
              >
                Loan Application
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default HomePage;
